import { Button } from "../atoms";

interface PriceRangeValue {
  min: number;
  max: number;
}

interface PriceRangeSliderProps {
  value: PriceRangeValue;
  onChange: (value: PriceRangeValue) => void;
  onClear: () => void;
  onApply: () => void;
  showActions?: boolean;
  hideTitle?: boolean;
}

/**
 * Price range slider component.
 * Dual-thumb slider for selecting min/max price ($0-$300).
 * Unselected track: neutral-20, selected track: primary-110.
 */
export function PriceRangeSlider({
  value,
  onChange,
  onClear,
  onApply,
  showActions = true,
  hideTitle = false,
}: PriceRangeSliderProps) {
  const MIN = 0;
  const MAX = 300;

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMin = Math.min(Number(e.target.value), value.max);
    onChange({ ...value, min: newMin });
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMax = Math.max(Number(e.target.value), value.min);
    onChange({ ...value, max: newMax });
  };

  // Calculate percentage for slider backgrounds
  const minPercent = ((value.min - MIN) / (MAX - MIN)) * 100;
  const maxPercent = ((value.max - MIN) / (MAX - MIN)) * 100;

  return (
    <div className="space-y-4 ">
      {!hideTitle && (
        <h3 className="text-primary-110 font-semibold mb-4">Precio</h3>
      )}
      {/* Slider container */}
      <div className="relative h-4 flex items-center">
        {/* Background track */}
        <div className="absolute w-full h-1 bg-neutral-20 rounded-full" />

        {/* Selected track */}
        <div
          className="absolute h-1 bg-primary-110 rounded-full"
          style={{
            left: `${minPercent}%`,
            right: `${100 - maxPercent}%`,
          }}
        />

        {/* Min input */}
        <input
          type="range"
          min={MIN}
          max={MAX}
          value={value.min}
          onChange={handleMinChange}
          className="absolute w-full h-1 top-1.5 z-10 appearance-none bg-transparent cursor-pointer pointer-events-none accent-primary-110"
          style={{
            WebkitAppearance: "slider-horizontal",
          }}
        />

        {/* Max input */}
        <input
          type="range"
          min={MIN}
          max={MAX}
          value={value.max}
          onChange={handleMaxChange}
          className="absolute w-full h-1 top-1.5 z-10 appearance-none bg-transparent cursor-pointer pointer-events-none accent-primary-110"
          style={{
            WebkitAppearance: "slider-horizontal",
          }}
        />
      </div>

      {/* Price display */}
      <div className="flex justify-between items-center gap-4">
        <div className="flex-1">
          <div className="text-black text-l font-bold">${value.min}</div>
        </div>
        <div className="flex-1 text-right">
          <div className="text-black text-l font-bold">${value.max}</div>
        </div>
      </div>

      {/* Action buttons */}
      {showActions && (
        <div className="flex gap-3 pt-1">
          <Button
            className="w-[130px] border border-primary-110 bg-white text-primary-110 hover:bg-primary-10"
            onClick={() => {
              onChange({ min: 0, max: 300 });
              onClear();
            }}
          >
            Limpiar
          </Button>

          <Button
            className="w-[130px] border border-primary-110 bg-secondary-120 hover:bg-secondary-120/90"
            onClick={onApply}
          >
            Aplicar
          </Button>
        </div>
      )}

      {/* CSS for slider thumbs */}
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: var(--color-primary-110);
          cursor: pointer;
          pointer-events: auto;
          border: 2px solid var(--color-primary-110);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        input[type="range"]::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: var(--color-primary-110);
          cursor: pointer;
          pointer-events: auto;
          border: 2px solid var(--color-primary-110);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
      `}</style>
    </div>
  );
}
